import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Gift, Sparkle, CheckCircle, Warning, Receipt, QrCode, Scan, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

// Modern, business-agnostic: warm off-white + inky near-black + electric coral accent + warm cream for the voucher gold-stamp feel.
const PAGE = "#F7F5F1";        // warm off-white paper
const PAGE_LIGHT = "#FFFFFF";
const INK = "#0F1014";         // near-black
const INK_SOFT = "#4A4C52";
const CORAL = "#FF5A3C";       // bright editorial accent
const CORAL_DEEP = "#D33F23";
const CREAM = "#F4E4B0";       // soft gold for voucher preview stamp
const CREAM_DEEP = "#C9A544";
const RULE = "#E4E2DB";

// Demo business name — deliberately generic so viewers picture their own brand here.
const BRAND = "Pennyweight & Co.";
const BRAND_TAGLINE = "Independent · est. 2014";

const PRESET_AMOUNTS = [25, 50, 75, 100, 150];

const ISSUED_SEED = [
  { code: "VV-9K2-7PQ", amount: 75, balance: 75, to: "Jenny Walsh", from: "Ben", message: "Happy birthday — treat yourself", issued: "12 Mar 2026", expires: "12 Mar 2027" },
  { code: "VV-4M8-3RT", amount: 100, balance: 40, to: "Dad", from: "The kids", message: "Father's Day — your pick", issued: "18 Jun 2025", expires: "18 Jun 2026" },
  { code: "VV-7C1-0XB", amount: 50, balance: 0, to: "Priya K.", from: "Office Secret Santa", message: "", issued: "20 Dec 2025", expires: "20 Dec 2026" },
];

const TABS = [
  { key: "shop", label: "Gift shop" },
  { key: "redeem", label: "Till · redeem" },
  { key: "dashboard", label: "Owner dashboard" },
];

/**
 * Normalise a user-typed code into canonical VV-XXX-XXX form.
 * Accepts: "vv9k27pq", "9K2-7PQ", "vv 9k2 7pq", "9k27pq", "w-9k2-7pq" (typo) etc.
 * Strategy: strip non-alphanumeric, uppercase, then drop any leading letters before the first digit.
 */
function normaliseCode(raw) {
  if (!raw) return "";
  const cleaned = raw.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  // Drop any leading letters (VV, V, W-typo, any prefix) — take everything from the first digit onward.
  const firstDigit = cleaned.search(/[0-9]/);
  const body = firstDigit >= 0 ? cleaned.slice(firstDigit) : cleaned;
  if (body.length >= 6) {
    return `VV-${body.slice(0, 3)}-${body.slice(3, 6)}`;
  }
  return body ? `VV-${body}` : "";
}

export default function VoucherVault() {
  const [tab, setTab] = useState("shop");
  const [vouchers, setVouchers] = useState(ISSUED_SEED);
  const navigate = useNavigate();

  const addVoucher = (v) => setVouchers((vs) => [v, ...vs]);
  const redeemOn = (code, amount) => {
    setVouchers((vs) => vs.map((v) => (v.code === code ? { ...v, balance: Math.max(0, v.balance - amount) } : v)));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: PAGE, color: INK, fontFamily: "'Manrope', sans-serif" }} data-testid="voucher-vault-interactive">
      <header className="border-b" style={{ backgroundColor: PAGE_LIGHT, borderColor: RULE }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/demos/voucher-vault" className="opacity-50" style={{ color: INK }}><ArrowLeft size={18} /></Link>
            {/* Platform lock-up */}
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 flex items-center justify-center" style={{ backgroundColor: INK }}>
                <Gift size={15} weight="fill" color={CORAL} />
              </div>
              <div className="text-sm font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Voucher Vault</div>
            </div>
            {/* Divider */}
            <div className="h-6 w-px hidden sm:block" style={{ backgroundColor: RULE }} />
            {/* Demo business */}
            <div className="hidden sm:block">
              <div className="text-sm font-bold leading-none" style={{ fontFamily: "'Outfit', serif", fontStyle: "italic" }}>{BRAND} <span className="font-mono text-[10px] opacity-60 font-normal not-italic" style={{ fontStyle: "normal" }}>· Concept mock</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mt-0.5">{BRAND_TAGLINE}</div>
            </div>
          </div>
          <button onClick={() => navigate("/consultation?demo=voucher-vault")} className="text-xs font-semibold px-4 py-2 border" style={{ borderColor: INK, color: INK }} data-testid="voucher-book-cta">
            Talk to us about this
          </button>
        </div>
        <nav className="max-w-6xl mx-auto px-6 flex gap-6 border-t" style={{ borderColor: RULE }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className="relative py-3 text-sm font-medium" style={{ color: tab === t.key ? INK : INK_SOFT }} data-testid={`voucher-tab-${t.key}`}>
              {t.label}
              {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: CORAL }} />}
            </button>
          ))}
        </nav>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-10">
        {tab === "shop" && <ShopTab addVoucher={addVoucher} />}
        {tab === "redeem" && <RedeemTab vouchers={vouchers} redeemOn={redeemOn} />}
        {tab === "dashboard" && <DashboardTab vouchers={vouchers} />}
      </section>

      <footer className="border-t mt-8 py-10" style={{ backgroundColor: PAGE_LIGHT, borderColor: RULE }}>
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-3">Concept · interactive mock</p>
          <p className="max-w-md mx-auto text-sm opacity-80 mb-5 italic" style={{ fontFamily: "'Outfit', serif" }}>"One platform, any business. Your brand on the voucher, your till takes it, your liability on the dashboard."</p>
          <Link to="/consultation?demo=voucher-vault" className="inline-flex items-center gap-2 text-white px-5 py-3 text-sm font-semibold" style={{ backgroundColor: INK }}>Book a 30-min consultation <ArrowRight size={14} weight="bold" /></Link>
        </div>
      </footer>
    </div>
  );
}

function ShopTab({ addVoucher }) {
  const [amount, setAmount] = useState(75);
  const [custom, setCustom] = useState("");
  const [to, setTo] = useState("");
  const [from, setFrom] = useState("");
  const [message, setMessage] = useState("");
  const [step, setStep] = useState("design"); // design | checkout | sent
  const [code, setCode] = useState("");

  const chosen = custom ? Number(custom) : amount;

  const goToCheckout = () => {
    if (!to || !from) { toast.error("Add who it's to and from"); return; }
    if (!chosen || chosen < 5) { toast.error("Choose an amount of £5 or more"); return; }
    setStep("checkout");
  };

  const pay = () => {
    const newCode = "VV-" + Math.random().toString(36).slice(2, 5).toUpperCase() + "-" + Math.random().toString(36).slice(2, 5).toUpperCase();
    setCode(newCode);
    addVoucher({ code: newCode, amount: chosen, balance: chosen, to, from, message, issued: "today", expires: "in 12 months" });
    setStep("sent");
    toast.success("Payment successful · voucher sent");
  };

  const reset = () => { setStep("design"); setTo(""); setFrom(""); setMessage(""); setCustom(""); setAmount(75); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
      {/* LEFT — form */}
      <div className="lg:col-span-3">
        <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">Gift shop</p>
        <h1 className="text-5xl font-black tracking-tighter mb-6 leading-[0.95]" style={{ fontFamily: "'Outfit', sans-serif" }}>The gift that travels<br/>by email.</h1>

        {step === "design" && (
          <div className="space-y-6">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest opacity-60 mb-2">Amount</label>
              <div className="flex flex-wrap gap-2">
                {PRESET_AMOUNTS.map((a) => (
                  <button key={a} onClick={() => { setAmount(a); setCustom(""); }} className="px-5 py-3 font-bold text-sm border-2 transition-all" style={{ borderColor: amount === a && !custom ? CORAL : RULE, backgroundColor: amount === a && !custom ? CORAL : "white", color: amount === a && !custom ? "white" : INK }} data-testid={`voucher-amount-${a}`}>£{a}</button>
                ))}
                <input type="number" placeholder="Custom £" value={custom} onChange={(e) => setCustom(e.target.value)} className="px-4 py-3 border-2 w-32 text-sm" style={{ borderColor: custom ? CORAL : RULE }} data-testid="voucher-amount-custom" />
              </div>
            </div>
            <Field label="Recipient name"><input value={to} onChange={(e) => setTo(e.target.value)} placeholder="e.g. Mum" className="input-vv" data-testid="voucher-to" /></Field>
            <Field label="From"><input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="e.g. The kids" className="input-vv" data-testid="voucher-from" /></Field>
            <Field label="Personal message (optional)"><textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className="input-vv" data-testid="voucher-message" /></Field>
            <button onClick={goToCheckout} className="w-full py-4 font-bold text-white text-base tracking-tight" style={{ backgroundColor: INK }} data-testid="voucher-continue">Continue to checkout · £{chosen || 0} →</button>
          </div>
        )}

        {step === "checkout" && (
          <div className="space-y-5">
            <button onClick={() => setStep("design")} className="text-xs font-mono opacity-60 hover:opacity-100">‹ Back to design</button>
            <div className="border-2 p-5 space-y-3" style={{ borderColor: RULE, backgroundColor: "white" }}>
              <Line label="Gift voucher" value={`£${chosen}.00`} />
              <Line label="Card fee" value="£0.00" />
              <div className="h-px" style={{ backgroundColor: RULE }} />
              <Line label={<b>Total</b>} value={<b style={{ color: CORAL }}>£{chosen}.00</b>} />
            </div>
            <div className="border-2 p-5 space-y-3" style={{ borderColor: RULE, backgroundColor: "white" }}>
              <p className="text-[10px] font-mono uppercase tracking-widest opacity-60">Card details (demo)</p>
              <input className="input-vv" placeholder="4242 4242 4242 4242" data-testid="voucher-card" />
              <div className="flex gap-2">
                <input className="input-vv" placeholder="12 / 27" />
                <input className="input-vv" placeholder="CVC" />
              </div>
            </div>
            <button onClick={pay} className="w-full py-4 font-bold text-white tracking-tight" style={{ backgroundColor: CORAL }} data-testid="voucher-pay">Pay £{chosen} — send voucher</button>
          </div>
        )}

        {step === "sent" && (
          <div className="space-y-5" data-testid="voucher-sent">
            <div className="p-6 text-center" style={{ backgroundColor: INK, color: "white" }}>
              <CheckCircle size={44} weight="fill" className="mx-auto mb-3" style={{ color: CORAL }} />
              <h2 className="text-2xl font-black mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Sent · £{chosen}</h2>
              <p className="text-sm opacity-80">Emailed to {to} with your message from {from}.</p>
            </div>
            <div className="border-2 p-5" style={{ borderColor: RULE, backgroundColor: "white" }}>
              <p className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Voucher code</p>
              <p className="font-mono text-2xl font-bold" style={{ color: CORAL }}>{code}</p>
            </div>
            <button onClick={reset} className="text-sm font-semibold px-5 py-3 border-2" style={{ borderColor: INK, color: INK }}>Send another</button>
          </div>
        )}
      </div>

      {/* RIGHT — preview */}
      <div className="lg:col-span-2 lg:sticky lg:top-28">
        <p className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-3 text-center">Preview</p>
        <div className="relative overflow-hidden shadow-[6px_6px_0_0_rgba(15,16,20,0.12)]" style={{ backgroundColor: INK, color: "white", aspectRatio: "7/4" }} data-testid="voucher-preview">
          {/* corner slash */}
          <div className="absolute top-0 right-0 w-24 h-24" style={{ background: `linear-gradient(135deg, transparent 50%, ${CORAL} 50%)` }} />
          {/* inner rule */}
          <div className="absolute inset-4 border" style={{ borderColor: CREAM + "66" }} />
          <div className="relative h-full flex flex-col justify-between p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] font-mono uppercase tracking-[0.4em]" style={{ color: CREAM }}>Gift Voucher</p>
                <p className="text-sm font-bold mt-1" style={{ fontFamily: "'Outfit', serif", fontStyle: "italic" }}>{BRAND}</p>
              </div>
              <Sparkle size={18} weight="fill" style={{ color: "white" }} className="relative z-10" />
            </div>
            <div>
              <div className="text-6xl font-black leading-none tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>£{chosen || 0}</div>
              <p className="text-xs italic mt-3 opacity-80 leading-relaxed" style={{ fontFamily: "'Outfit', serif" }}>{message || (to ? `For ${to}` : "Your message appears here")}</p>
            </div>
            <div className="flex items-end justify-between">
              <div className="space-y-0.5">
                {to && <p className="text-[10px] font-mono opacity-80">TO · <b>{to.toUpperCase()}</b></p>}
                {from && <p className="text-[10px] font-mono opacity-80">FROM · <b>{from.toUpperCase()}</b></p>}
              </div>
              <div className="flex items-center justify-center w-10 h-10" style={{ backgroundColor: "white" }}>
                <QrCode size={24} weight="regular" color={INK} />
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-xs opacity-60 mt-4">Delivered by email · can be printed · valid 12 months</p>
      </div>

      <style>{`
        .input-vv {
          width: 100%; border: 2px solid ${RULE}; padding: 0.75rem 0.9rem;
          font-family: 'Manrope', sans-serif; font-size: 0.95rem; background: white;
        }
        .input-vv:focus { outline: none; border-color: ${CORAL}; }
      `}</style>
    </div>
  );
}

function RedeemTab({ vouchers, redeemOn }) {
  const [code, setCode] = useState("");
  const [found, setFound] = useState(null);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState("");

  const lookup = (raw = code) => {
    const normalised = normaliseCode(raw);
    if (!normalised || normalised.length < 9) {
      setFound(null);
      setError("Codes look like VV-XXX-XXX · 6 characters after the VV.");
      return;
    }
    const v = vouchers.find((x) => x.code === normalised);
    if (v) { setFound(v); setError(null); setAmount(""); setCode(normalised); }
    else { setFound(null); setError(`No voucher found for ${normalised}. Double-check the code or scan the QR.`); }
  };

  const tryCode = (sampleCode) => {
    setCode(sampleCode);
    lookup(sampleCode);
  };

  const redeem = () => {
    const a = Number(amount);
    if (!a || a <= 0 || a > found.balance) { toast.error("Enter an amount up to the balance"); return; }
    redeemOn(found.code, a);
    setFound({ ...found, balance: found.balance - a });
    setAmount("");
    toast.success(`Redeemed £${a}`);
  };

  const simulateScan = () => {
    // Grab an unused voucher for the scan simulation
    const unused = vouchers.find((v) => v.balance === v.amount) || vouchers[0];
    toast.success("QR scanned ✓");
    tryCode(unused.code);
  };

  const samples = vouchers.slice(0, 3);

  return (
    <div className="max-w-2xl mx-auto">
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">Till</p>
      <h1 className="text-5xl font-black tracking-tighter mb-6 leading-[0.95]" style={{ fontFamily: "'Outfit', sans-serif" }}>Redeem a voucher</h1>
      <p className="opacity-70 mb-6 max-w-md">Scan the QR on the email, or key the code in. It's fine if the customer forgot the VV — we'll recognise it.</p>

      <div className="flex gap-2 mb-3">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
          placeholder="Type or paste — e.g. 9K27PQ or VV-9K2-7PQ"
          className="flex-1 px-4 py-4 border-2 font-mono text-base tracking-wider"
          style={{ borderColor: error ? CORAL : RULE }}
          data-testid="voucher-redeem-code"
        />
        <button onClick={() => lookup()} className="px-5 py-4 font-bold text-white flex items-center gap-2" style={{ backgroundColor: INK }} data-testid="voucher-redeem-lookup">
          Look up <ArrowRight size={16} weight="bold" />
        </button>
      </div>
      <button onClick={simulateScan} className="w-full mb-6 py-3 border-2 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white transition-colors" style={{ borderColor: INK, color: INK, borderStyle: "dashed" }} data-testid="voucher-redeem-scan">
        <Scan size={18} weight="bold" /> Or scan the QR on the customer's voucher
      </button>

      {/* Sample codes — clearly tappable chips (demo helper) */}
      <div className="mb-6 p-4 border-2" style={{ borderColor: RULE, backgroundColor: "white" }}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-mono uppercase tracking-widest opacity-60">Demo helper · tap any code</p>
          <span className="text-[9px] font-mono opacity-50">Simulates a real voucher</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {samples.map((s) => (
            <button
              key={s.code}
              onClick={() => tryCode(s.code)}
              className="flex items-center gap-2 px-3 py-2 border-2 font-mono text-xs hover:bg-[color:var(--hoverbg)] transition-colors"
              style={{ borderColor: RULE, "--hoverbg": CORAL + "15" }}
              data-testid={`voucher-sample-${s.code}`}
            >
              <Copy size={12} weight="bold" style={{ color: CORAL }} />
              <b style={{ color: INK }}>{s.code}</b>
              <span className="opacity-60">· {s.balance === 0 ? "spent" : s.balance === s.amount ? `£${s.amount} unused` : `£${s.balance} left`}</span>
            </button>
          ))}
        </div>
      </div>

      {error && <div className="p-4 border-2 flex items-center gap-3 mb-4" style={{ borderColor: CORAL, backgroundColor: CORAL + "12" }} data-testid="voucher-redeem-error">
        <Warning size={20} weight="fill" style={{ color: CORAL_DEEP }} />
        <span className="text-sm" style={{ color: CORAL_DEEP }}>{error}</span>
      </div>}

      {found && (
        <div className="border-2 p-6 space-y-4" style={{ borderColor: INK, backgroundColor: "white" }} data-testid="voucher-redeem-found">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest opacity-60">For</p>
              <h3 className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{found.to}</h3>
              <p className="text-sm opacity-70">From {found.from} · {found.issued}</p>
              {found.message && <p className="text-sm italic mt-2" style={{ fontFamily: "'Outfit', serif" }}>"{found.message}"</p>}
            </div>
            <div className="text-right">
              <div className="text-5xl font-black" style={{ color: found.balance > 0 ? CORAL : INK_SOFT, fontFamily: "'Outfit', sans-serif" }}>£{found.balance}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{found.balance > 0 ? `of £${found.amount} remaining` : "Fully redeemed"}</div>
            </div>
          </div>
          {found.balance > 0 && (
            <div className="pt-4 border-t flex gap-2" style={{ borderColor: RULE }}>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Redeem how much (max £${found.balance})`} className="flex-1 px-4 py-3 border-2" style={{ borderColor: RULE }} data-testid="voucher-redeem-amount" />
              <button onClick={() => setAmount(String(found.balance))} className="px-3 text-xs border-2 font-semibold" style={{ borderColor: RULE }}>Full</button>
              <button onClick={redeem} className="px-5 py-3 font-bold text-white" style={{ backgroundColor: CORAL }} data-testid="voucher-redeem-apply">Redeem →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DashboardTab({ vouchers }) {
  const sold = vouchers.reduce((t, v) => t + v.amount, 0);
  const redeemed = vouchers.reduce((t, v) => t + (v.amount - v.balance), 0);
  const liability = vouchers.reduce((t, v) => t + v.balance, 0);

  return (
    <div>
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">Owner dashboard</p>
      <h1 className="text-5xl font-black tracking-tighter mb-8 leading-[0.95]" style={{ fontFamily: "'Outfit', sans-serif" }}>Vault health</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard label="Sold" value={`£${sold}`} sub={`${vouchers.length} vouchers`} accent={INK} />
        <StatCard label="Redeemed" value={`£${redeemed}`} sub={`${Math.round((redeemed / Math.max(1, sold)) * 100)}% of sold`} accent={CORAL} />
        <StatCard label="Outstanding liability" value={`£${liability}`} sub="On your books" accent={CREAM_DEEP} />
      </div>
      <div className="border-2" style={{ borderColor: RULE, backgroundColor: "white" }} data-testid="voucher-ledger">
        <div className="px-5 py-3 border-b" style={{ borderColor: RULE }}><p className="text-xs font-mono uppercase tracking-widest font-bold">Voucher ledger</p></div>
        {vouchers.map((v) => {
          const pct = Math.round((v.balance / v.amount) * 100);
          return (
            <div key={v.code} className="px-5 py-3 border-b last:border-b-0 flex items-center gap-4" style={{ borderColor: RULE }}>
              <Receipt size={18} weight="duotone" style={{ color: CORAL }} />
              <div className="flex-1 min-w-0">
                <div className="flex gap-3 flex-wrap"><b style={{ fontFamily: "'Outfit', sans-serif" }}>{v.to}</b> <span className="font-mono text-xs opacity-60">{v.code}</span></div>
                <p className="text-xs opacity-60">from {v.from} · issued {v.issued}</p>
              </div>
              <div className="w-24 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "#F0EEE8" }}>
                <div className="h-full" style={{ width: pct + "%", backgroundColor: v.balance === 0 ? INK_SOFT : CORAL }} />
              </div>
              <div className="text-right w-24">
                <div className="font-bold" style={{ color: v.balance === 0 ? INK_SOFT : INK }}>£{v.balance}</div>
                <div className="text-[10px] font-mono opacity-60">of £{v.amount}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="p-5 border-2" style={{ borderColor: RULE, backgroundColor: "white" }}>
      <p className="text-[10px] font-mono uppercase tracking-widest opacity-60">{label}</p>
      <div className="text-4xl font-black mt-1 tracking-tighter" style={{ color: accent, fontFamily: "'Outfit', sans-serif" }}>{value}</div>
      <p className="text-xs opacity-60 mt-1">{sub}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Line({ label, value }) {
  return <div className="flex items-baseline justify-between"><span className="text-sm opacity-70">{label}</span><span>{value}</span></div>;
}
